import { useState } from 'react';

import { FilterItem } from '@/components/ui/custom/ComplexFilter/types';
import { useTableFilters } from '@/hooks/useTableFilters';
import { Datable } from '@/shared/types/collections';

type FilterValueGetter<T> = (item: T) => string | string[] | null | undefined;

interface UseResourceListOptions<T> {
  /** localStorage key for the active filters, as `useTableFilters` takes it. */
  storageKey: string;
  /** User-preferences key for saved filter views. */
  preferencesKey: string;
  /** For each filter id, the value or values an item carries for it. */
  filterValues: Record<string, FilterValueGetter<T>>;
  /** The text a search matches against. */
  searchText: (item: T) => (string | null | undefined)[];
  /** List order. Defaults to newest first. */
  sort?: (a: T, b: T) => number;
}

const toArray = (value: string | string[] | null | undefined) => {
  if (value == null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};

const matchesFilters = <T>(
  item: T,
  filters: FilterItem[],
  filterValues: Record<string, FilterValueGetter<T>>
) =>
  filters.every(({ key, values, exclude }) => {
    const getValues = filterValues[key];
    if (!getValues || values.length === 0) {
      return true;
    }
    const hit = toArray(getValues(item)).some((value) => values.includes(value));
    return exclude ? !hit : hit;
  });

const byNewest = (a: Datable, b: Datable) =>
  new Date(b.created ?? 0).getTime() - new Date(a.created ?? 0).getTime();

/** Most recently used first; never-used items last, newest first among them. */
export const byLastUsed = <T extends Datable & { last_used_at: string | null }>(a: T, b: T) => {
  if (a.last_used_at && b.last_used_at) {
    return new Date(b.last_used_at).getTime() - new Date(a.last_used_at).getTime();
  }
  if (a.last_used_at || b.last_used_at) {
    return a.last_used_at ? -1 : 1;
  }
  return byNewest(a, b);
};

/**
 * Search, filters and saved views for a developer-settings list. Filter state lives in
 * `useTableFilters`; `toolbarProps` goes straight to `FilterToolbar`.
 */
export const useResourceList = <T extends Datable>(
  items: T[],
  {
    storageKey,
    preferencesKey,
    filterValues,
    searchText,
    sort = byNewest,
  }: UseResourceListOptions<T>
) => {
  const [searchTerm, setSearchTerm] = useState('');
  const tableFilters = useTableFilters({ storageKey, preferencesKey });
  const query = searchTerm.trim().toLowerCase();

  const visibleItems = items
    .filter((item) => matchesFilters(item, tableFilters.filters, filterValues))
    .filter(
      (item) => !query || searchText(item).some((text) => text?.toLowerCase().includes(query))
    )
    .sort(sort);

  return {
    visibleItems,
    toolbarProps: {
      searchTerm,
      onSearchChange: setSearchTerm,
      filters: tableFilters.filters,
      setFilter: tableFilters.setFilter,
      removeFilter: tableFilters.removeFilter,
      resetFilters: tableFilters.resetFilters,
      activeFilterCount: tableFilters.activeFilterCount,
      savedViews: tableFilters.savedViews,
      activeView: tableFilters.activeView,
      hasUnsavedChanges: tableFilters.hasUnsavedChanges,
      onSaveView: tableFilters.saveView,
      onUpdateView: tableFilters.updateView,
      onDeleteView: tableFilters.deleteView,
      onRenameView: tableFilters.renameView,
      onApplyView: tableFilters.applyView,
      onClearActiveView: tableFilters.clearActiveView,
    },
  };
};
